import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { haptic, isHosted, openExternal } from "../platform/telegram";
import { WaitlistInline } from "../components/AppGate";
import { EmptyState, ErrorState, Skeleton } from "../components/states";
import {
  fetchCourses,
  fetchLearnFeed,
  itemShareUrl,
  saveCourses,
  syncLearnUrl,
  type CanonicalCourse,
  type LearnItem,
} from "./api";
import { ensureYouTubeApi, soundPreference, type YtPlayer } from "./ytApi";
import "./learn.css";

/**
 * The Learn feed — the one renderer (Distribution §4). This same component is
 * the standalone web feed at join.freshman.app/learn, the miniapp's Learn
 * section, and the reference implementation the SwiftUI renderers copy.
 *
 * Feed mechanics per CONTENT_ENGINE_PLAN.md: per-course, syllabus-week order,
 * no ranking; an interactive item is most items in the video-less cut; a
 * waitlist CTA card rides every Nth position (browser only).
 */

const CTA_EVERY = 6;

/** Chip sentinel: every selected course merged into one feed. */
const ALL = "all";

/** Formats that render as drills behind a video's Test button. */
const DRILL_FORMATS = new Set(["quiz", "flashcards", "mistake", "pairs", "sort"]);

interface FeedCard {
  kind: "video" | "item" | "cta";
  item?: LearnItem;
  drills?: LearnItem[];
  key: string;
}

export function LearnFeed({
  initialCourseId = null,
  initialItemId = null,
}: {
  initialCourseId?: string | null;
  initialItemId?: string | null;
}) {
  const [catalog, setCatalog] = useState<CanonicalCourse[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  // The toggled-on pills; empty means All. All never toggles off directly —
  // it takes over only when the last course pill is toggled off.
  const [active, setActive] = useState<string[]>(
    initialCourseId && initialCourseId !== ALL ? [initialCourseId] : [],
  );
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    void fetchCourses().then((state) => {
      if (state) {
        setCatalog(state.catalog);
        setSelected(state.selected);
        // A single saved course lights its own pill; more start on All.
        setActive((current) =>
          current.length === 0 && state.selected.length === 1 ? [state.selected[0]] : current,
        );
      }
      setCoursesLoaded(true);
    });
  }, []);

  const ready = useMemo(() => new Set(catalog.filter((c) => c.ready).map((c) => c.id)), [catalog]);

  // A deep-linked course counts as browsable even if it isn't saved yet.
  const chipCourses = useMemo(() => {
    const ids = [...selected];
    for (const id of active) if (!ids.includes(id)) ids.unshift(id);
    return ids
      .map((id) => catalog.find((c) => c.id === id))
      .filter((c): c is CanonicalCourse => Boolean(c));
  }, [selected, active, catalog]);

  if (!coursesLoaded) {
    const skeleton = (
      <div className="stack" style={{ marginTop: "var(--space-3)" }}>
        <Skeleton height={320} radius={16} />
      </div>
    );
    return isHosted ? skeleton : <div className="lf-pad">{skeleton}</div>;
  }

  // "All" with nothing selected (a bare /learn/all deep link) has no feed to
  // merge — the picker is the answer there too.
  const needsPicker = pickerOpen || (active.length === 0 && selected.length === 0);

  if (needsPicker) {
    const picker = (
      <CoursePicker
        catalog={catalog}
        selected={selected}
        onDone={(ids) => {
          setSelected(ids);
          setPickerOpen(false);
          // Keep whichever pills survive the edit; a lone course lights its
          // own pill, anything else lands on All.
          setActive((current) => {
            const kept = current.filter((id) => ids.includes(id));
            if (kept.length > 0) return kept;
            return ids.length === 1 ? [ids[0]] : [];
          });
          void saveCourses(ids);
        }}
      />
    );
    return isHosted ? picker : <div className="lf-pad">{picker}</div>;
  }

  const toggleCourse = (id: string) => {
    // Toggle the pill; turning on any pill knocks All off, and turning the
    // last pill off hands the feed back to All.
    const next = active.includes(id) ? active.filter((x) => x !== id) : [...active, id];
    // The only rendered pill can't toggle itself off — there's no All chip
    // to hand the feed to.
    if (next.length === 0 && chipCourses.length === 1) return;
    haptic.select();
    setActive(next);
    syncLearnUrl(next.length === 1 ? next[0] : ALL, null);
  };

  const chipRow = (
    <div className={isHosted ? "chips lf-course-chips" : "lf-web__chips"}>
      {chipCourses.length > 1 ? (
        <button
          type="button"
          className={`chip${active.length === 0 ? " chip--active" : ""}`}
          onClick={() => {
            if (active.length === 0) return; // already All — only a pill turns it off
            haptic.select();
            setActive([]);
            syncLearnUrl(ALL, null);
          }}
        >
          All
        </button>
      ) : null}
      {chipCourses.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`chip${active.includes(c.id) ? " chip--active" : ""}`}
          onClick={() => toggleCourse(c.id)}
        >
          {c.name}
        </button>
      ))}
      <button
        type="button"
        className="chip"
        aria-label="Edit courses"
        onClick={() => setPickerOpen(true)}
      >
        <i className="fa-solid fa-plus" aria-hidden="true" /> Courses
      </button>
    </div>
  );

  // The merge order follows the chip row, not toggle order, so the same set
  // of pills always builds the same feed. Ids the catalog doesn't know pass
  // through so a bogus deep link still surfaces the error state.
  const activeIds = chipCourses.map((c) => c.id).filter((id) => active.includes(id));
  const feedCourseIds = active.length === 0 ? selected : activeIds.length > 0 ? activeIds : active;
  const feed =
    feedCourseIds.length > 0 ? (
      <CourseFeed
        courseIds={feedCourseIds}
        initialItemId={initialItemId}
        isReady={feedCourseIds.some((id) => ready.has(id))}
      />
    ) : null;

  if (isHosted) {
    return (
      <>
        {chipRow}
        {feed}
      </>
    );
  }

  // Browser: immersive. The chip row floats under the Home bar as a slim
  // translucent rail; the feed runs full-bleed edge to edge beneath both.
  return (
    <div className="lf-web">
      {chipRow}
      {feed}
    </div>
  );
}

/* ---------------- Picker ---------------- */

function CoursePicker({
  catalog,
  selected,
  onDone,
}: {
  catalog: CanonicalCourse[];
  selected: string[];
  onDone: (ids: string[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set(selected));
  const toggle = (id: string) => {
    haptic.select();
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const ordered = [...catalog].sort((a, b) => Number(b.ready) - Number(a.ready));

  return (
    <div className="lf-picker">
      <h1 className="t-title">Pick your courses</h1>
      <p className="lf-picker__sub">Your feed is built from what you're actually taking.</p>
      <div className="lf-picker__grid">
        {ordered.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`lf-picker__course${picked.has(c.id) ? " lf-picker__course--on" : ""}`}
            onClick={() => toggle(c.id)}
          >
            <span className="lf-picker__name">{c.name}</span>
            {c.ready ? (
              <span className="lf-picker__badge">Live</span>
            ) : (
              <span className="lf-picker__badge lf-picker__badge--soon">Soon</span>
            )}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="btn btn--primary lf-picker__done"
        disabled={picked.size === 0}
        onClick={() => onDone([...picked])}
      >
        Build my feed
      </button>
    </div>
  );
}

/* ---------------- Feed ---------------- */

/**
 * The All feed's merge: one k-way pass over the per-course feeds, always
 * taking the course whose next item sits in the earliest syllabus week and
 * rotating on ties — so week 1 alternates courses instead of running one
 * course front to back. Each course's own order is preserved.
 */
function interleaveByWeek(feeds: LearnItem[][]): LearnItem[] {
  const cursor = feeds.map(() => 0);
  const out: LearnItem[] = [];
  const total = feeds.reduce((n, f) => n + f.length, 0);
  let last = -1;
  while (out.length < total) {
    let best = -1;
    for (let step = 1; step <= feeds.length; step++) {
      const f = (last + step) % feeds.length;
      if (cursor[f] >= feeds[f].length) continue;
      if (best === -1 || feeds[f][cursor[f]].syllabusWeek < feeds[best][cursor[best]].syllabusWeek)
        best = f;
    }
    out.push(feeds[best][cursor[best]]);
    cursor[best] += 1;
    last = best;
  }
  return out;
}

function CourseFeed({
  courseIds,
  initialItemId,
  isReady,
}: {
  courseIds: string[];
  initialItemId: string | null;
  isReady: boolean;
}) {
  const [items, setItems] = useState<LearnItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const didScroll = useRef(false);

  const coursesKey = courseIds.join(",");
  const load = useCallback(() => {
    setFailed(false);
    setItems(null);
    const ids = coursesKey.split(",");
    void Promise.all(ids.map((id) => fetchLearnFeed(id))).then((feeds) => {
      const loaded = feeds.filter((f): f is NonNullable<typeof f> => Boolean(f));
      // Partial outages still make a feed; only a total miss is a failure.
      if (loaded.length === 0) setFailed(true);
      else setItems(interleaveByWeek(loaded.map((f) => f.items)));
    });
  }, [coursesKey]);

  useEffect(load, [load]);

  // Pure-video feed (iOS parity): each topic's video is the card, its
  // interactive items ride behind the Test button as drills. Topics with no
  // video yet keep their inline cards so a video-less course still has a feed.
  const cards = useMemo<FeedCard[]>(() => {
    if (!items) return [];
    // Topic keys carry the course — the All feed mixes courses in one list.
    const topicKey = (item: LearnItem) => `${item.courseId}#${item.courseTopicId}`;
    const drillsByTopic = new Map<string, LearnItem[]>();
    const videoTopics = new Set<string>();
    for (const item of items) {
      if (item.format === "video") videoTopics.add(topicKey(item));
      else if (DRILL_FORMATS.has(item.format)) {
        const list = drillsByTopic.get(topicKey(item)) ?? [];
        list.push(item);
        drillsByTopic.set(topicKey(item), list);
      }
    }
    const out: FeedCard[] = [];
    let count = 0;
    // Waitlist CTA woven in, browser only (Distribution §3).
    const maybeCta = () => {
      count += 1;
      if (!isHosted && count % CTA_EVERY === 0) out.push({ kind: "cta", key: `cta-${count}` });
    };
    for (const item of items) {
      if (item.format === "video") {
        out.push({
          kind: "video",
          item,
          drills: drillsByTopic.get(topicKey(item)) ?? [],
          key: item.id,
        });
        maybeCta();
      } else if (DRILL_FORMATS.has(item.format) && !videoTopics.has(topicKey(item))) {
        out.push({ kind: "item", item, key: item.id });
        maybeCta();
      }
    }
    return out;
  }, [items]);

  // A deep link to a drill lands on its topic's video with the sheet open.
  const drillTarget = useMemo(() => {
    if (!items || !initialItemId) return null;
    const item = items.find((x) => x.id === initialItemId);
    if (!item || !DRILL_FORMATS.has(item.format)) return null;
    const video = items.find(
      (x) =>
        x.format === "video" &&
        x.courseId === item.courseId &&
        x.courseTopicId === item.courseTopicId,
    );
    return video ? { videoId: video.id, drillId: item.id } : null;
  }, [items, initialItemId]);

  // Deep link: land on the shared item, then let the user keep swiping.
  useEffect(() => {
    if (didScroll.current || !initialItemId || cards.length === 0) return;
    didScroll.current = true;
    const targetId = drillTarget?.videoId ?? initialItemId;
    const node = containerRef.current?.querySelector(`[data-item-id="${targetId}"]`);
    node?.scrollIntoView({ block: "start" });
  }, [cards, initialItemId, drillTarget]);

  // The address bar follows the card in view — every position is shareable.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isHosted) return;
    // The URL names the item's own course, so a position shared off the All
    // feed deep-links into that course's feed.
    const courseById = new Map((items ?? []).map((item) => [item.id, item.courseId]));
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = entry.target.getAttribute("data-item-id");
          setActiveCardId(id);
          // CTA cards keep the previous item's URL — they aren't shareable.
          const course = id ? courseById.get(id) : undefined;
          if (id && course) syncLearnUrl(course, id);
        }
      },
      { root: container, threshold: 0.6 },
    );
    for (const node of container.querySelectorAll("[data-item-id]")) observer.observe(node);
    return () => observer.disconnect();
  }, [cards, items]);

  // Non-feed states sit in normal padded flow — full-bleed is for cards only.
  const framed = (node: React.ReactNode) =>
    isHosted ? <>{node}</> : <div className="lf-pad">{node}</div>;

  if (failed) return framed(<ErrorState title="The feed didn’t load" onRetry={load} />);
  if (!items) {
    return framed(
      <div className="stack" style={{ marginTop: "var(--space-3)" }}>
        <Skeleton height={320} radius={16} />
        <Skeleton height={320} radius={16} />
      </div>,
    );
  }
  if (items.length === 0) {
    return framed(
      <EmptyState
        icon="fa-graduation-cap"
        title={isReady ? "This feed is filling up" : "This course is coming soon"}
        body={
          isReady
            ? "Fresh items land here as they clear review — check back shortly."
            : "You picked it, so it's on the build list. The first three feeds are live now."
        }
      />,
    );
  }

  // Video lifecycle wants video positions: the active video plays, its
  // neighbours hold a cued player, everything else is a poster.
  const videoIds = cards.filter((c) => c.kind === "video").map((c) => c.item!.id);
  const videoPos = new Map(videoIds.map((id, i) => [id, i]));
  const activeVideoPos = videoPos.get(
    activeCardId ?? drillTarget?.videoId ?? initialItemId ?? videoIds[0] ?? "",
  );

  return (
    <div className={`lf-feed${isHosted ? "" : " lf-feed--web"}`} ref={containerRef}>
      {cards.map((card) => {
        if (card.kind === "cta") return <WaitlistCard key={card.key} markerId={card.key} />;
        if (card.kind === "video") {
          const pos = videoPos.get(card.item!.id)!;
          return (
            <VideoFeedCard
              key={card.key}
              item={card.item!}
              drills={card.drills!}
              active={activeVideoPos === pos}
              near={activeVideoPos !== undefined && Math.abs(activeVideoPos - pos) === 1}
              autoOpenDrillId={drillTarget?.videoId === card.item!.id ? drillTarget.drillId : null}
            />
          );
        }
        return <FeedItemCard key={card.key} item={card.item!} />;
      })}
    </div>
  );
}

/* ---------------- Item chrome ---------------- */

function useShare(item: LearnItem) {
  const [copied, setCopied] = useState(false);
  const share = () => {
    const url = itemShareUrl(item.courseId, item.id);
    if (isHosted) {
      openExternal(`https://t.me/share/url?url=${encodeURIComponent(url)}`);
      return;
    }
    void navigator.clipboard?.writeText(url).then(() => {
      haptic.success();
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return { copied, share };
}

/** The interactive renderers, minus video — used by inline cards and the
 * Test sheet alike. */
function DrillBody({ item }: { item: LearnItem }) {
  return (
    <>
      {item.format === "quiz" ? <QuizCard payload={item.payload} /> : null}
      {item.format === "flashcards" ? <FlashcardsCard payload={item.payload} /> : null}
      {item.format === "mistake" ? <MistakeCard payload={item.payload} /> : null}
      {item.format === "pairs" ? <PairsCard payload={item.payload} /> : null}
      {item.format === "sort" ? <SortCard payload={item.payload} /> : null}
    </>
  );
}

/** Inline card for a topic that has no video yet — the pre-video layout,
 * restyled dark to sit in the video feed. */
function FeedItemCard({ item }: { item: LearnItem }) {
  const { copied, share } = useShare(item);

  return (
    <section className="lf-card lf-dark" data-item-id={item.id}>
      <header className="lf-card__head">
        <span className="lf-card__topic">
          Week {item.syllabusWeek} · {item.topicTitle}
        </span>
        <button type="button" className="lf-card__share" onClick={share} aria-label="Share">
          {copied ? (
            <>
              <i className="fa-solid fa-check" aria-hidden="true" /> Copied
            </>
          ) : (
            <i className="fa-solid fa-arrow-up-from-bracket" aria-hidden="true" />
          )}
        </button>
      </header>

      <div className="lf-card__body">
        <DrillBody item={item} />
      </div>

      <footer className="lf-card__foot">
        <span className="lf-card__source">{item.sourceRef.split(" — ")[0]}</span>
      </footer>
    </section>
  );
}

/* ---------------- Renderers ---------------- */

/** Full-bleed video card — the iOS YouTubeVideoCell layout: video centered on
 * black, scrim at the bottom, channel + title bottom-left, action rail
 * bottom-right with Test (the topic's drills) above share. */
function VideoFeedCard({
  item,
  drills,
  active,
  near,
  autoOpenDrillId,
}: {
  item: LearnItem;
  drills: LearnItem[];
  active: boolean;
  near: boolean;
  autoOpenDrillId: string | null;
}) {
  const payload = item.payload;
  const videoId = String(payload.videoId ?? "");
  const title = String(payload.title ?? "");
  const channel = String(payload.channel ?? "");
  const durationSeconds = Number(payload.durationSeconds ?? 0);
  const duration = `${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, "0")}`;
  const { copied, share } = useShare(item);
  const [sheetAt, setSheetAt] = useState<number | null>(null);
  const autoOpened = useRef(false);

  // Deep-linked drill: open the sheet on its page once the card exists.
  useEffect(() => {
    if (!autoOpenDrillId || autoOpened.current) return;
    const index = drills.findIndex((d) => d.id === autoOpenDrillId);
    if (index >= 0) {
      autoOpened.current = true;
      setSheetAt(index);
    }
  }, [autoOpenDrillId, drills]);

  const poster = (onTap?: () => void) => (
    <button
      type="button"
      className="lf-video__poster"
      onClick={onTap}
      aria-label="Play"
      disabled={!onTap}
    >
      <img src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`} alt="" loading="lazy" />
      <span className="lf-video__play">
        <i className="fa-solid fa-play" aria-hidden="true" />
      </span>
      <span className="lf-video__dur">{duration}</span>
    </button>
  );

  return (
    <section className="lf-card lf-card--video lf-dark" data-item-id={item.id}>
      <div className="lf-video__stage">
        {isHosted ? (
          <EmbedVideoFrame videoId={videoId} title={title} poster={poster} />
        ) : (
          <BrowserVideoFrame
            videoId={videoId}
            title={title}
            active={active && sheetAt === null}
            near={near}
            poster={poster}
          />
        )}
      </div>
      <div className="lf-video__overlay">
        <div className="lf-video__meta">
          <div className="lf-video__byline">
            <span className="lf-video__avatar" aria-hidden="true">
              {channel.charAt(0)}
            </span>
            <span className="lf-video__channel">{channel}</span>
          </div>
          <span className="lf-video__title">{title}</span>
        </div>
        <div className="lf-rail">
          {drills.length > 0 ? (
            <button
              type="button"
              className="lf-rail__btn"
              onClick={() => {
                haptic.select();
                setSheetAt(0);
              }}
            >
              <i className="fa-solid fa-clipboard-list" aria-hidden="true" />
              <span>Test</span>
            </button>
          ) : null}
          <button type="button" className="lf-rail__btn" onClick={share} aria-label="Share">
            <i
              className={copied ? "fa-solid fa-check" : "fa-solid fa-arrow-up-from-bracket"}
              aria-hidden="true"
            />
            {copied ? <span>Copied</span> : null}
          </button>
        </div>
      </div>
      {sheetAt !== null ? (
        <TestSheet drills={drills} initialIndex={sheetAt} onClose={() => setSheetAt(null)} />
      ) : null}
    </section>
  );
}

/** The drill pager behind the Test button — the iOS LearnTestSheet: one drill
 * per horizontal page over black, page dots, X to close. */
function TestSheet({
  drills,
  initialIndex,
  onClose,
}: {
  drills: LearnItem[];
  initialIndex: number;
  onClose: () => void;
}) {
  const pagerRef = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(initialIndex);

  useEffect(() => {
    const pager = pagerRef.current;
    if (pager && initialIndex > 0) pager.scrollLeft = pager.clientWidth * initialIndex;
  }, [initialIndex]);

  // Portaled: ancestors animate transforms, which would trap position:fixed
  // and leave the sheet under the shell bars.
  return createPortal(
    <div className="lf-sheet lf-dark" role="dialog" aria-label="Test">
      <button type="button" className="lf-sheet__close" onClick={onClose} aria-label="Close">
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
      <div
        className="lf-sheet__pager"
        ref={pagerRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          setPage(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
        }}
      >
        {drills.map((drill) => (
          <div className="lf-sheet__page" key={drill.id}>
            <span className="lf-sheet__topic">
              Week {drill.syllabusWeek} · {drill.topicTitle}
            </span>
            <DrillBody item={drill} />
            <span className="lf-card__source">{drill.sourceRef.split(" — ")[0]}</span>
          </div>
        ))}
      </div>
      {drills.length > 1 ? (
        <div className="lf-sheet__dots" aria-hidden="true">
          {drills.map((drill, i) => (
            <span
              key={drill.id}
              className={`lf-sheet__dot${i === page ? " lf-sheet__dot--on" : ""}`}
            />
          ))}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

/** Bare embed, tap-to-play. The Telegram build always uses this (YouTube
 * inside Telegram's webview is too flaky for API players or autoplay), and
 * the browser build degrades to it when shields/adblock eat the API script —
 * plain embed iframes survive blockers that kill www.youtube.com/iframe_api. */
function EmbedVideoFrame({
  videoId,
  title,
  poster,
}: {
  videoId: string;
  title: string;
  poster: (onTap?: () => void) => React.ReactNode;
}) {
  const [playing, setPlaying] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);

  // Tear the embed down when the card scrolls away so audio never follows
  // the user down the feed. Poster comes back; tapping resumes from zero.
  useEffect(() => {
    if (!playing || !frameRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) setPlaying(false);
      },
      { threshold: 0.4 },
    );
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, [playing]);

  return (
    <div className="lf-video__frame" ref={frameRef}>
      {playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      ) : (
        poster(() => setPlaying(true))
      )}
    </div>
  );
}

/** Browser build: a managed IFrame-API player per live card, mirroring the
 * iOS YouTubePlayerPool. Active card autoplays (muted until the session's
 * first unmute), the neighbouring cards hold a cued player so scrolling to
 * them starts instantly, everything further is a plain poster. Videos loop
 * on end; a dead player falls back to the poster and a tap rebuilds it. */
function BrowserVideoFrame({
  videoId,
  title,
  active,
  near,
  poster,
}: {
  videoId: string;
  title: string;
  active: boolean;
  near: boolean;
  poster: (onTap?: () => void) => React.ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YtPlayer | null>(null);
  const mountRef = useRef<HTMLElement | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(!soundPreference.on);
  const [blocked, setBlocked] = useState(false); // API script eaten by shields/adblock
  const [generation, setGeneration] = useState(0); // bump to rebuild after an error

  const teardown = () => {
    playerRef.current?.destroy();
    playerRef.current = null;
    mountRef.current?.remove();
    mountRef.current = null;
  };

  useEffect(() => {
    if (!active && !near) {
      teardown();
      setStarted(false);
      return;
    }
    let cancelled = false;
    ensureYouTubeApi()
      .catch(() => {
        if (!cancelled) setBlocked(true);
        return null;
      })
      .then((yt) => {
        if (!yt || cancelled || playerRef.current || !hostRef.current) return;
        const mount = document.createElement("div");
        hostRef.current.appendChild(mount);
        mountRef.current = mount;
        playerRef.current = new yt.Player(mount, {
          videoId,
          playerVars: { playsinline: 1, rel: 0, mute: soundPreference.on ? 0 : 1 },
          events: {
            onReady: (event) => {
              if (activeRef.current) event.target.playVideo();
            },
            onStateChange: (event) => {
              if (event.data === yt.PlayerState.PLAYING) setStarted(true);
              // Loop: the feed convention is short videos that repeat.
              if (event.data === yt.PlayerState.ENDED && activeRef.current) {
                event.target.seekTo(0, true);
                event.target.playVideo();
              }
            },
            onError: () => {
              teardown();
              setStarted(false);
            },
          },
        });
      });
    return () => {
      cancelled = true;
      teardown();
    };
  }, [videoId, active || near, generation]);

  // Active flips play/pause on the already-built player.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (active) {
      if (soundPreference.on) {
        player.unMute();
        setMuted(false);
      }
      player.playVideo();
    } else {
      player.pauseVideo();
    }
  }, [active]);

  const unmute = () => {
    soundPreference.on = true;
    playerRef.current?.unMute();
    setMuted(false);
  };

  if (blocked) {
    return <EmbedVideoFrame videoId={videoId} title={title} poster={poster} />;
  }

  return (
    <div className="lf-video__frame" ref={hostRef}>
      {started ? null : (
        <div className="lf-video__cover">{poster(() => setGeneration((g) => g + 1))}</div>
      )}
      {started && muted ? (
        <button type="button" className="lf-video__sound" onClick={unmute} aria-label="Unmute">
          <i className="fa-solid fa-volume-xmark" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

interface QuizPayload {
  question: string;
  choices: string[];
  correctIndex: number;
  explanation: string;
}

function QuizCard({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as unknown as QuizPayload;
  const [answered, setAnswered] = useState<number | null>(null);

  const pick = (i: number) => {
    if (answered !== null) return;
    if (i === p.correctIndex) haptic.success();
    else haptic.tap();
    setAnswered(i);
  };

  return (
    <div className="lf-quiz">
      <h2 className="lf-h">{p.question}</h2>
      <div className="lf-quiz__choices">
        {p.choices.map((choice, i) => {
          let state = "";
          if (answered !== null) {
            if (i === p.correctIndex) state = " lf-choice--correct";
            else if (i === answered) state = " lf-choice--wrong";
            else state = " lf-choice--dim";
          }
          return (
            <button
              key={i}
              type="button"
              className={`lf-choice${state}`}
              onClick={() => pick(i)}
              disabled={answered !== null}
            >
              {choice}
            </button>
          );
        })}
      </div>
      {answered !== null ? <p className="lf-quiz__why">{p.explanation}</p> : null}
    </div>
  );
}

interface FlashcardsPayload {
  title: string;
  cards: { front: string; back: string }[];
}

function FlashcardsCard({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as unknown as FlashcardsPayload;
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const card = p.cards[index];

  const advance = (dir: 1 | -1) => {
    haptic.select();
    setFlipped(false);
    setIndex((i) => Math.min(p.cards.length - 1, Math.max(0, i + dir)));
  };

  return (
    <div className="lf-flash">
      <h2 className="lf-h lf-flash__title">{p.title}</h2>
      <button
        type="button"
        className={`lf-flash__card${flipped ? " lf-flash__card--flipped" : ""}`}
        onClick={() => {
          haptic.tap();
          setFlipped((f) => !f);
        }}
      >
        <span className="lf-flash__face lf-flash__face--front">{card.front}</span>
        <span className="lf-flash__face lf-flash__face--back">{card.back}</span>
      </button>
      <div className="lf-flash__nav">
        <button
          type="button"
          className="lf-flash__paddle"
          aria-label="Previous card"
          disabled={index === 0}
          onClick={() => advance(-1)}
        >
          <i className="fa-solid fa-chevron-left" aria-hidden="true" />
        </button>
        <span className="lf-flash__count">
          {index + 1} / {p.cards.length}
        </span>
        <button
          type="button"
          className="lf-flash__paddle"
          aria-label="Next card"
          disabled={index === p.cards.length - 1}
          onClick={() => advance(1)}
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

interface MistakePayload {
  title: string;
  misconception: string;
  correction: string;
  tip: string;
}

function MistakeCard({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as unknown as MistakePayload;
  return (
    <div className="lf-mistake">
      <h2 className="lf-h">{p.title}</h2>
      <div className="lf-mistake__block lf-mistake__block--myth">
        <span className="lf-mistake__label">The trap</span>
        <p>{p.misconception}</p>
      </div>
      <div className="lf-mistake__block lf-mistake__block--fact">
        <span className="lf-mistake__label">Actually</span>
        <p>{p.correction}</p>
      </div>
      <p className="lf-mistake__tip">
        <i className="fa-regular fa-lightbulb" aria-hidden="true" /> {p.tip}
      </p>
    </div>
  );
}

interface PairsPayload {
  title: string;
  instruction: string;
  pairs: { left: string; right: string }[];
}

/** Deterministic shuffle — no Math.random so a shared item looks the same for
 * everyone (and re-renders don't reshuffle). */
function stableShuffle<T>(list: T[], seed: string): T[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return list
    .map((value, i) => ({ value, sort: ((h = (h * 1103515245 + 12345) | 0), h ^ i) }))
    .sort((a, b) => a.sort - b.sort)
    .map((x) => x.value);
}

function PairsCard({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as unknown as PairsPayload;
  const rights = useMemo(
    () =>
      stableShuffle(
        p.pairs.map((x) => x.right),
        p.pairs[0]?.left ?? "seed",
      ),
    [p.pairs],
  );
  const [activeLeft, setActiveLeft] = useState<string | null>(null);
  const [solved, setSolved] = useState<Set<string>>(new Set());
  const [miss, setMiss] = useState<string | null>(null);

  const tapLeft = (left: string) => {
    if (solved.has(left)) return;
    haptic.select();
    setActiveLeft(left);
  };
  const tapRight = (right: string) => {
    if (!activeLeft) return;
    const pair = p.pairs.find((x) => x.left === activeLeft);
    if (pair?.right === right) {
      haptic.success();
      setSolved((s) => new Set(s).add(activeLeft));
      setActiveLeft(null);
    } else {
      haptic.tap();
      setMiss(right);
      setTimeout(() => setMiss(null), 450);
    }
  };
  const done = solved.size === p.pairs.length;
  const solvedRights = new Set(p.pairs.filter((x) => solved.has(x.left)).map((x) => x.right));

  return (
    <div className="lf-pairs">
      <h2 className="lf-h">{p.title}</h2>
      <p className="lf-sub">{p.instruction}</p>
      <div className="lf-pairs__grid">
        <div className="lf-pairs__col">
          {p.pairs.map((x) => (
            <button
              key={x.left}
              type="button"
              className={`lf-tile${activeLeft === x.left ? " lf-tile--active" : ""}${solved.has(x.left) ? " lf-tile--solved" : ""}`}
              onClick={() => tapLeft(x.left)}
            >
              {x.left}
            </button>
          ))}
        </div>
        <div className="lf-pairs__col">
          {rights.map((right) => (
            <button
              key={right}
              type="button"
              className={`lf-tile${solvedRights.has(right) ? " lf-tile--solved" : ""}${miss === right ? " lf-tile--miss" : ""}`}
              onClick={() => tapRight(right)}
            >
              {right}
            </button>
          ))}
        </div>
      </div>
      {done ? (
        <p className="lf-done">
          <i className="fa-solid fa-check" aria-hidden="true" /> All matched
        </p>
      ) : null}
    </div>
  );
}

interface SortPayload {
  title: string;
  instruction: string;
  bins: string[];
  sortItems: { text: string; bin: string }[];
}

function SortCard({ payload }: { payload: Record<string, unknown> }) {
  const p = payload as unknown as SortPayload;
  const [index, setIndex] = useState(0);
  const [missed, setMissed] = useState(false);
  const [score, setScore] = useState(0);
  const current = p.sortItems[index];
  const done = index >= p.sortItems.length;

  const drop = (bin: string) => {
    if (done) return;
    if (bin === current.bin) {
      haptic.success();
      setScore((s) => s + (missed ? 0 : 1));
      setMissed(false);
      setIndex((i) => i + 1);
    } else {
      haptic.tap();
      setMissed(true);
    }
  };

  return (
    <div className="lf-sort">
      <h2 className="lf-h">{p.title}</h2>
      <p className="lf-sub">{p.instruction}</p>
      {done ? (
        <p className="lf-done">
          <i className="fa-solid fa-check" aria-hidden="true" /> {score} / {p.sortItems.length} on
          the first try
        </p>
      ) : (
        <>
          <div className={`lf-sort__item${missed ? " lf-sort__item--miss" : ""}`}>
            {current.text}
          </div>
          <div className="lf-sort__bins">
            {p.bins.map((bin) => (
              <button key={bin} type="button" className="lf-tile" onClick={() => drop(bin)}>
                {bin}
              </button>
            ))}
          </div>
          <span className="lf-sort__count">
            {index + 1} / {p.sortItems.length}
          </span>
        </>
      )}
    </div>
  );
}

/* ---------------- Waitlist CTA card (browser only) ---------------- */

function WaitlistCard({ markerId }: { markerId: string }) {
  // data-item-id makes the observer treat the CTA as a (non-shareable) card,
  // so any playing video pauses while it's in view.
  return (
    <section className="lf-card lf-card--cta" data-item-id={markerId}>
      <div className="lf-card__body">
        <h2 className="lf-h">The full app goes further.</h2>
        <p className="lf-sub">
          Your courses, your people, your deadlines — all in one place. Drop your email and you're
          on the list.
        </p>
        <WaitlistInline />
      </div>
    </section>
  );
}
